// src/pages/ChatPage.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import {
  FiSend,
  FiImage,
  FiVideo,
  FiFileText,
  FiMoreVertical,
  FiChevronLeft,
  FiDownload,
  FiMaximize2,
  FiX,
  FiLock,
  FiPaperclip,
  FiExternalLink
} from "react-icons/fi";
import api from "../services/api";
import { useAppData } from "../context/AppDataContext";
import { useDropzone } from "react-dropzone";
import ImageViewer from "../components/ImageViewer";
import axios from "axios";

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

const Avatar = ({ profile, product, size = "w-12 h-12" }) => {
  const { t } = useTranslation();
  
  if (profile?.logo_url) {
    return <img src={profile.logo_url} className={`${size} rounded-full object-cover border border-gray-200/50 shadow-sm`} alt="" />;
  }
  
  if (product?.thumbnail_url) {
    return <img src={product.thumbnail_url} className={`${size} rounded-full object-cover border border-gray-200/50 shadow-sm`} alt="" />;
  }

  const name = profile?.company_name || profile?.full_name || 
               (profile?.first_name ? `${profile.first_name} ${profile.last_name || ""}` : null) || 
               t("common.user");
  const initial = name.trim().charAt(0).toUpperCase();
  
  return (
    <div className={`${size} rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center border border-gray-200/50 shadow-sm shrink-0`}>
      <span className="text-gray-600 font-bold text-lg">{initial}</span>
    </div>
  );
};

const ProductAvatar = ({ product, profile, size = "w-12 h-12" }) => {
  const { t } = useTranslation();
  
  if (product?.thumbnail_url) {
    return <img src={product.thumbnail_url} className={`${size} rounded-xl object-cover border border-gray-200/50 shadow-sm shrink-0`} alt="" />;
  }
  
  if (product?.featured_image_url) {
    return <img src={product.featured_image_url} className={`${size} rounded-xl object-cover border border-gray-200/50 shadow-sm shrink-0`} alt="" />;
  }

  if (profile?.logo_url) {
    return <img src={profile.logo_url} className={`${size} rounded-full object-cover border border-gray-200/50 shadow-sm shrink-0`} alt="" />;
  }
  
  const name = profile?.company_name || profile?.full_name || 
               (profile?.first_name ? `${profile.first_name} ${profile.last_name || ""}` : null) || 
               t("common.user");
  const initial = name.trim().charAt(0).toUpperCase();
  
  return (
    <div className={`${size} rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center border border-gray-200/50 shadow-sm shrink-0`}>
      <span className="text-gray-600 font-bold text-lg">{initial}</span>
    </div>
  );
};

const MessageContent = ({ message, openViewer }) => {
  const { t } = useTranslation();

  if (!message.image_url) {
    return <p className="text-sm font-medium leading-relaxed">{message.text}</p>;
  }

  const isImage = message.image_url.includes("/image/upload/");
  const isVideo = message.image_url.includes("/video/upload/");

  let content;

  if (isImage) {
    content = (
      <div className="rounded-2xl overflow-hidden bg-white shadow-sm border border-gray-100 max-w-[280px] group cursor-pointer" onClick={() => openViewer(message.image_url, "image")}>
        <div className="relative aspect-square overflow-hidden">
          <img src={message.image_url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt="Attached" />
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <FiMaximize2 className="text-white text-2xl" />
          </div>
        </div>
      </div>
    );
  } else if (isVideo) {
    content = (
      <div className="rounded-2xl overflow-hidden bg-white shadow-sm border border-gray-100 max-w-[280px] group cursor-pointer" onClick={() => openViewer(message.image_url, "video")}>
        <div className="relative aspect-square bg-black flex items-center justify-center">
          <video src={`${message.image_url}#t=0.1`} className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/30 group-hover:scale-110 transition-transform">
              <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[14px] border-l-white border-b-[8px] border-b-transparent ml-1" />
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    const filename = message.original_filename || t("common.downloadFile");
    const fileExtension = filename.split(".").pop()?.toUpperCase() || "FILE";

    content = (
      <div className="rounded-2xl overflow-hidden bg-white shadow-sm border border-gray-100 max-w-[280px]">
        <a href={message.image_url} target="_blank" rel="noopener noreferrer" className="block p-4 hover:bg-gray-50 transition-colors no-underline">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#ff7a00] text-white rounded-xl flex flex-col items-center justify-center shadow-lg shadow-orange-100 shrink-0">
              <FiFileText size={20} />
              <span className="text-[8px] font-black uppercase tracking-tighter mt-0.5">{fileExtension}</span>
            </div>
            <div className="min-w-0">
              <div className="font-bold text-xs text-gray-900 truncate">{filename}</div>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1 flex items-center gap-1">
                <FiDownload size={10} />
                {t("common.download")}
              </div>
            </div>
          </div>
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {message.text && <p className="text-sm font-medium leading-relaxed">{message.text}</p>}
      {content}
    </div>
  );
};

const ChatPage = () => {
  const { t } = useTranslation();
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshData } = useAppData();

  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [fileToUpload, setFileToUpload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const [viewerSrc, setViewerSrc] = useState(null);
  const [viewerType, setViewerType] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterMode, setFilterMode] = useState("all");
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const attachmentMenuRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (attachmentMenuRef.current && !attachmentMenuRef.current.contains(e.target)) {
        setShowAttachmentMenu(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const filteredConversations = conversations.filter((convo) => {
    const otherUser = convo.participant_profiles?.find(p => p.id !== user?.id);
    const convoName = (
      convo.product?.name ||
      otherUser?.company_name ||
      otherUser?.full_name ||
      (otherUser?.first_name ? `${otherUser.first_name} ${otherUser.last_name || ""}` : "") ||
      t("common.user")
    ).toLowerCase();
    const latestText = (
      convo.last_message_text ||
      convo.messages?.[convo.messages?.length - 1]?.text ||
      ""
    ).toLowerCase();
    
    // Search filter
    if (
      searchQuery &&
      !convoName.includes(searchQuery.toLowerCase()) &&
      !latestText.includes(searchQuery.toLowerCase())
    ) {
      return false;
    }
    
    // Unread filter
    if (filterMode === "unread") {
      return convo.unread_messages_count > 0;
    }
    return true;
  });
  
  const openViewer = (src, type = "image") => {
    setViewerSrc(src);
    setViewerType(type);
  };

  const closeViewer = () => {
    setViewerSrc(null);
    setViewerType(null);
  };

  const formatTimestamp = (isoString) => {
    if (!isoString) return "";
    try {
      const date = new Date(isoString);
      date.setTime(date.getTime() + 3 * 60 * 60 * 1000);
      const hours = String(date.getUTCHours()).padStart(2, "0");
      const minutes = String(date.getUTCMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    } catch (e) {
      return "";
    }
  };

  const scrollToBottom = (behavior = "smooth") => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  };

  const onFileSelect = useCallback(
    (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        const firstError = fileRejections[0].errors[0];
        alert(firstError.code === "file-too-large" ? `${t("chatPage.errors.fileTooLarge")} ${MAX_FILE_SIZE / 1024 / 1024}MB.` : `${t("common.error")}: ${firstError.message}`);
        return;
      }
      if (acceptedFiles.length > 0) {
        setFileToUpload(acceptedFiles[0]);
      }
    },
    [t]
  );

  const { open: openImagePicker, getInputProps: getImageInputProps } = useDropzone({
    onDrop: onFileSelect,
    multiple: false,
    noClick: true,
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp", ".bmp", ".tiff", ".svg"] },
    maxSize: MAX_FILE_SIZE,
  });

  const { open: openDocPicker, getInputProps: getDocInputProps } = useDropzone({
    onDrop: onFileSelect,
    multiple: false,
    noClick: true,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "text/plain": [".txt"],
    },
    maxSize: MAX_FILE_SIZE,
  });

  const { open: openVideoPicker, getInputProps: getVideoInputProps } = useDropzone({
    onDrop: onFileSelect,
    multiple: false,
    noClick: true,
    accept: { "video/*": [".mp4", ".mov", ".webm", ".m4v"] },
    maxSize: MAX_FILE_SIZE,
  });

  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.get("/conversations/");
      const convos = (res.data.results || res.data).sort((a, b) => 
        new Date(b.last_message_timestamp || b.created_at) - new Date(a.last_message_timestamp || a.created_at)
      );
      setConversations(convos);
    } catch (error) {
      console.error("Failed to fetch conversations", error);
    }
  }, []);

  const fetchMessages = useCallback(async (id) => {
    if (!id || id === "all") {
      setActiveConversation(null);
      setMessages([]);
      return;
    }
    try {
      const res = await api.get(`/conversations/${id}/messages/`);
      setMessages(res.data.results || res.data);
      if (refreshData) refreshData();
      // Scroll to bottom immediately on first load of messages
      setTimeout(() => scrollToBottom("auto"), 100);
    } catch (error) {
      navigate("/chat/all");
    }
  }, [navigate, refreshData]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchConversations();
      setLoading(false);
    };
    init();
  }, [fetchConversations]);

  const location = useLocation();
  const initAttempted = useRef(false);

  useEffect(() => {
    const initDirectChat = async () => {
      if (conversationId && conversationId !== "all") return;
      if (initAttempted.current) return;

      const params = new URLSearchParams(location.search);
      const targetUserId = params.get("user");
      const targetProductId = params.get("product");

      if (targetUserId) {
        initAttempted.current = true;
        setLoading(true);
        try {
          const res = await api.post("/conversations/", {
            participants: [parseInt(targetUserId)],
            product: targetProductId ? parseInt(targetProductId) : null
          });
          const newConvo = res.data;
          await fetchConversations();
          navigate(`/chat/${newConvo.id}`, { replace: true });
        } catch (error) {
          console.error("Failed to initialize direct chat", error);
          setLoading(false);
        }
      }
    };
    initDirectChat();
  }, [conversationId, location.search, navigate, fetchConversations]);

  useEffect(() => {
    const currentConvo = conversations.find(c => c.id.toString() === conversationId);
    setActiveConversation(currentConvo || null);
    fetchMessages(conversationId);
  }, [conversationId, conversations, fetchMessages]);

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (messages.length > 0) {
      const container = scrollContainerRef.current;
      if (container) {
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
        if (isNearBottom || messages[messages.length - 1].sender.id === user?.id) {
          scrollToBottom("auto");
        }
      }
    }
  }, [messages, user?.id]);

  useEffect(() => {
    if (!activeConversation) return;
    const intervalId = setInterval(async () => {
      try {
        const res = await api.get(`/conversations/${activeConversation.id}/messages/`);
        const latestMessages = res.data.results || res.data;
        if (latestMessages.length > messages.length) {
          setMessages(latestMessages);
        }
      } catch (error) {}
    }, 5000);
    return () => clearInterval(intervalId);
  }, [activeConversation, messages.length]);

  const handleSendMessage = useCallback(async (e, messageText) => {
    if (e) e.preventDefault();
    const textToSend = messageText || newMessage;
    if (!textToSend.trim() && !fileToUpload) return;
    if (!activeConversation) return;

    setSending(true);
    try {
      let fileUrl = null;
      let originalFilename = null;
      if (fileToUpload) {
        const cloudinaryFormData = new FormData();
        cloudinaryFormData.append("file", fileToUpload);
        cloudinaryFormData.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
        const cloudinaryRes = await axios.post(
          `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/auto/upload`,
          cloudinaryFormData
        );
        fileUrl = cloudinaryRes.data.secure_url;
        originalFilename = fileToUpload.name;
      }
      const payload = {};
      if (textToSend.trim()) payload.text = textToSend;
      if (fileUrl) {
        payload.image = fileUrl;
        payload.original_filename = originalFilename;
      }
      await api.post(`/conversations/${activeConversation.id}/messages/`, payload);
      await fetchMessages(activeConversation.id);
      setNewMessage("");
      setFileToUpload(null);
      await fetchConversations();
    } catch (error) {
      alert(t("chatPage.errors.failedToSend"));
    } finally {
      setSending(false);
    }
  }, [newMessage, fileToUpload, activeConversation, fetchConversations, fetchMessages, t]);

  const isMobileView = !conversationId || conversationId === "all";

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-10 h-10 border-2 border-gray-200 border-t-[#ff7a00] rounded-full animate-spin" />
        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{t("chatPage.connecting")}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 bg-[#f0f2f5] overflow-hidden relative font-sans" style={{ scrollbarGutter: "stable both-edges" }}>
      {/* WhatsApp Sidebar */}
      <aside className={`w-full lg:w-[420px] flex-shrink-0 border-r border-gray-300 bg-white flex flex-col ${!isMobileView ? "hidden lg:flex" : "flex"}`}>
        {/* Sidebar Header */}
        <div className="h-[60px] px-4 bg-[#f0f2f5] flex items-center justify-between border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-8 h-8 rounded-lg bg-chafadi-primary flex items-center justify-center text-white shrink-0 shadow-sm">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <path d="M16 10a4 4 0 0 1-8 0"></path>
              </svg>
            </div>
            <span className="font-extrabold text-[19px] text-[#111b21] tracking-tight">ChafadiChat</span>
          </div>
          <div className="flex items-center gap-4 text-gray-500">
             <button 
               onClick={() => window.open("https://chafadi.com", "_blank")}
               className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm border border-gray-200 text-chafadi-primary hover:scale-105 active:scale-95 transition-all"
               title="Open official website"
             >
               <FiExternalLink size={16} />
             </button>
          </div>
        </div>
        
        {/* Search Bar & Filters */}
        <div className="p-3 bg-white border-b border-gray-150 flex flex-col gap-3 shrink-0">
          <div className="bg-[#f0f2f5] rounded-xl px-3 h-10 flex items-center gap-3 border border-gray-200/50">
             <svg viewBox="0 0 24 24" width="18" height="18" className="text-gray-400 shrink-0"><path fill="currentColor" d="M15.009 13.805h-.636l-.22-.187a5.249 5.249 0 0 0 1.248-3.399 5.253 5.253 0 1 0-5.253 5.253 5.253 5.253 0 0 0 3.399-1.248l.187.22v.636L17.914 19 19 17.914l-3.991-4.109zm-4.856 0A3.559 3.559 0 0 1 6.594 10.25a3.559 3.559 0 0 1 3.559-3.559 3.559 3.559 0 0 1 3.559 3.559 3.559 3.559 0 0 1-3.559 3.559z"></path></svg>
             <input 
               type="text" 
               value={searchQuery}
               onChange={(e) => setSearchQuery(e.target.value)}
               placeholder="Search for products or chats..." 
               className="bg-transparent border-none outline-none text-sm w-full text-gray-700 placeholder:text-gray-400 focus:ring-0" 
             />
             <svg viewBox="0 0 24 24" width="18" height="18" className="text-gray-400 shrink-0 cursor-pointer hover:text-gray-600" onClick={() => setFilterMode(filterMode === "all" ? "unread" : "all")}><path fill="currentColor" d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"></path></svg>
          </div>
          
          {/* All / Unread Toggles */}
          <div className="flex gap-2.5">
            <button
              onClick={() => setFilterMode("all")}
              className={`flex-1 py-2 px-4 rounded-xl text-xs font-bold transition-all ${
                filterMode === "all"
                  ? "bg-[#ffebe5] text-chafadi-primary shadow-sm"
                  : "bg-white text-gray-500 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterMode("unread")}
              className={`flex-1 py-2 px-4 rounded-xl text-xs font-bold transition-all ${
                filterMode === "unread"
                  ? "bg-[#ffebe5] text-chafadi-primary shadow-sm"
                  : "bg-white text-gray-500 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              Unread
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-white custom-scrollbar">
          {filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8 opacity-60">
              <p className="text-sm text-gray-500">{t("chatPage.noConversations")}</p>
            </div>
          ) : (
            filteredConversations.map((convo) => {
              const otherUserProfile = convo.participant_profiles?.find(p => p.id !== user?.id);
              const isActive = conversationId === convo.id.toString();
              const latestMessage = convo.last_message_text ||
                convo.messages?.[convo.messages.length - 1]?.text ||
                convo.messages?.[convo.messages.length - 1]?.original_filename ||
                t("chatPage.noMessages");
              const hasUnread = convo.unread_messages_count > 0;
              return (
                <button
                  key={convo.id}
                  onClick={() => navigate(`/chat/${convo.id}`)}
                  className={`relative w-full h-[72px] pr-3 flex items-center gap-3 transition-colors border-b border-gray-100 ${
                    hasUnread ? "pl-6" : "pl-3"
                  } ${isActive ? "bg-[#ebebeb]" : "bg-white hover:bg-[#f5f6f6]"}`}
                >
                  {/* Left Orange Dot Indicator */}
                  {hasUnread && (
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-chafadi-primary shrink-0" />
                  )}

                  <ProductAvatar product={convo.product} profile={otherUserProfile} />
                  <div className="flex-1 min-w-0 pr-2">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-[15px] text-[#111b21] truncate text-left">
                        {convo.product?.name ||
                         otherUserProfile?.company_name || otherUserProfile?.full_name || 
                         (otherUserProfile?.first_name ? `${otherUserProfile.first_name} ${otherUserProfile.last_name || ""}` : null) || 
                         t("common.user")}
                      </span>
                      <span className="text-[11px] text-gray-400 font-medium shrink-0 ml-2">
                        {formatTimestamp(convo.last_message_timestamp)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                       <p className="text-[13px] text-gray-500 truncate flex-1 leading-normal text-left">
                         {latestMessage}
                       </p>
                       {hasUnread && (
                         <span className="bg-chafadi-primary text-white text-[11px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center shrink-0 ml-2 shadow-sm">
                           {convo.unread_messages_count}
                         </span>
                       )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Sidebar Footer - Back Button */}
        <div className="h-[60px] px-4 bg-white border-t border-gray-150 flex items-center justify-between shrink-0">
          <button 
            onClick={() => navigate("/")}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-gray-600 hover:bg-gray-100/80 active:bg-gray-200/80 transition-all text-sm font-bold border border-gray-200 shadow-sm"
          >
            <FiChevronLeft className="text-gray-500" size={18} />
            <span>Back</span>
            <span className="flex-1" />
            <FiChevronLeft className="rotate-180 text-gray-400 opacity-60" size={16} />
          </button>
        </div>
      </aside>

      {/* Main Chat Panel */}
      <main className={`flex-1 flex flex-col bg-[#efeae2] relative ${isMobileView ? "hidden lg:flex" : "flex"}`}>
        {/* Optional: Add a WhatsApp-style background pattern overlay */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')", backgroundSize: "400px" }} />

        {/* Chat Header */}
        <header className="h-[60px] px-4 border-b border-gray-200 flex items-center gap-3 bg-[#f0f2f5] sticky top-0 z-20 shrink-0">
          <button
            onClick={() => navigate("/chat/all")}
            className="lg:hidden text-gray-500 mr-1"
          >
            <FiChevronLeft size={24} />
          </button>

          {activeConversation ? (
            <>
              {activeConversation.product ? (
                <div 
                  className="flex flex-1 items-center gap-3 min-w-0 cursor-pointer group"
                  onClick={() => navigate(`/products/${activeConversation.product.id}`)}
                  title="View product details"
                >
                  <img 
                    src={activeConversation.product.thumbnail_url || activeConversation.product.featured_image_url || "/logo.png"} 
                    className="w-10 h-10 rounded-xl object-cover border border-gray-200/50 shadow-sm shrink-0 group-hover:scale-105 transition-transform" 
                    alt="" 
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-[#111b21] text-[15px] sm:text-[16px] truncate leading-tight group-hover:text-chafadi-primary transition-colors">
                      {activeConversation.product.name}
                    </h3>
                    <p className="text-[11px] text-gray-500 truncate mt-0.5 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                      Online
                    </p>
                  </div>
                </div>
              ) : (
                <div 
                  className="flex flex-1 items-center gap-3 min-w-0 cursor-pointer group"
                  onClick={() => navigate(`/users/${activeConversation.participant_profiles?.find(p => p.id !== user?.id)?.id}`)}
                  title="View user profile"
                >
                  <Avatar profile={activeConversation.participant_profiles?.find(p => p.id !== user?.id)} size="w-10 h-10" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-[#111b21] text-[15px] sm:text-[16px] truncate leading-tight group-hover:text-chafadi-primary transition-colors">
                      {activeConversation.participant_profiles?.find(p => p.id !== user?.id)?.company_name ||
                       activeConversation.participant_profiles?.find(p => p.id !== user?.id)?.full_name ||
                       t("common.user")}
                    </h3>
                    <p className="text-[11px] text-gray-500 truncate mt-0.5 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                      Online
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-[#111b21] text-[16px] truncate leading-tight">
                Chafadi Chat
              </h3>
              <p className="text-[11px] text-gray-500 truncate mt-0.5">
                {t("chatPage.selectPartner")}
              </p>
            </div>
          )}

          <div className="flex items-center gap-4 text-gray-500">
            {activeConversation?.product && (
              <button 
                onClick={() => navigate(`/products/${activeConversation.product.id}`)}
                className="hover:text-chafadi-primary text-gray-500 transition-colors"
                title="View product details"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <path d="M16 10a4 4 0 0 1-8 0"></path>
                </svg>
              </button>
            )}
            <button className="hover:text-gray-700 transition-colors"><FiMoreVertical size={20} /></button>
          </div>
        </header>

        {/* Messages Area */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto custom-scrollbar p-6 pb-2 relative z-10">
          {activeConversation ? (
            <div className="max-w-4xl mx-auto space-y-2 flex flex-col">
              {messages.map((message, idx) => {
                const isSent = message.sender.id === user?.id;
                return (
                  <div
                    key={message.id || idx}
                    className={`flex w-full ${isSent ? "justify-end" : "justify-start"}`}
                  >
                    <div className={`relative max-w-[85%] sm:max-w-[65%] px-3 py-1.5 rounded-lg shadow-sm text-[14.2px] leading-relaxed ${
                      isSent
                        ? "bg-[#dcf8c6] text-[#111b21] rounded-tr-none"
                        : "bg-white text-[#111b21] rounded-tl-none"
                    }`}>
                      <MessageContent message={message} openViewer={openViewer} />
                      <div className="flex justify-end mt-1 -mr-1">
                        <span className="text-[10px] text-gray-500 uppercase tracking-tighter opacity-70">
                          {formatTimestamp(message.timestamp)}
                        </span>
                      </div>
                      <div className={`absolute top-0 w-3 h-3 ${isSent ? "-right-2 bg-[#dcf8c6]" : "-left-2 bg-white"}`} style={{ clipPath: isSent ? "polygon(0 0, 0 100%, 100% 0)" : "polygon(100% 0, 100% 100%, 0 0)" }} />
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} className="h-4" />
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center px-10">
              <div className="w-48 h-48 opacity-20 mb-6 grayscale">
                <img src="/logo.png" className="w-full h-full object-contain" alt="" />
              </div>
              <h2 className="text-2xl font-light text-[#41525d] mb-3">Chafadi Web</h2>
              <p className="text-[#667781] text-[14px] max-w-md leading-relaxed">
                {t("chatPage.selectPartner")}
              </p>
              <div className="mt-8 flex items-center gap-2 text-gray-400 text-xs uppercase tracking-widest">
                <FiLock size={12} />
                <span>End-to-end encrypted</span>
              </div>
            </div>
          )}
        </div>

        {/* Input Area (always mounted to avoid jumps) */}
        <footer className="px-4 py-2 bg-[#f0f2f5] border-t border-gray-200 z-30 shrink-0">
          <div className="h-[52px] mb-2">
            {fileToUpload ? (
              <div className="h-full p-2 bg-white rounded-lg flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-orange-500">
                    {fileToUpload.type.startsWith("image") ? <FiImage size={20} /> : <FiFileText size={20} />}
                  </div>
                  <span className="text-xs font-medium text-gray-700 truncate max-w-[200px]">{fileToUpload.name}</span>
                </div>
                <button onClick={() => setFileToUpload(null)} className="text-gray-400 hover:text-red-500"><FiX /></button>
              </div>
            ) : null}
          </div>

          <form onSubmit={handleSendMessage} className="flex items-center gap-2 max-w-[1200px] mx-auto">
            <div className="relative" ref={attachmentMenuRef}>
              <button 
                type="button" 
                onClick={() => setShowAttachmentMenu(!showAttachmentMenu)} 
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  showAttachmentMenu 
                    ? "bg-chafadi-primary/10 text-chafadi-primary" 
                    : "text-[#54656f] hover:bg-gray-200/50"
                }`}
                disabled={!activeConversation}
                title="Attach file"
              >
                <FiPaperclip size={22} className={showAttachmentMenu ? "rotate-45 transition-transform" : "transition-transform"} />
              </button>

              {/* Smooth Dropdown popup upwards */}
              {showAttachmentMenu && (
                <div className="absolute bottom-12 left-0 w-40 bg-white border border-gray-200 rounded-2xl shadow-xl z-[100] p-1.5 flex flex-col gap-0.5 animate-slide-up">
                  <input {...getImageInputProps()} />
                  <button
                    type="button"
                    onClick={() => { openImagePicker(); setShowAttachmentMenu(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm text-gray-700 hover:bg-[#f5f6f6] transition-colors"
                  >
                    <FiImage size={18} className="text-[#007bfc]" />
                    <span className="font-semibold">Image</span>
                  </button>
                  
                  <input {...getVideoInputProps()} />
                  <button
                    type="button"
                    onClick={() => { openVideoPicker(); setShowAttachmentMenu(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm text-gray-700 hover:bg-[#f5f6f6] transition-colors"
                  >
                    <FiVideo size={18} className="text-[#00b050]" />
                    <span className="font-semibold">Video</span>
                  </button>

                  <input {...getDocInputProps()} />
                  <button
                    type="button"
                    onClick={() => { openDocPicker(); setShowAttachmentMenu(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm text-gray-700 hover:bg-[#f5f6f6] transition-colors"
                  >
                    <FiFileText size={18} className="text-[#ff7a00]" />
                    <span className="font-semibold">Document</span>
                  </button>
                </div>
              )}
            </div>

            <div className="flex-1 relative">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={activeConversation ? t("chatPage.typeMessage") : t("chatPage.selectPartner")}
                className="w-full h-10 bg-white border-none rounded-lg px-4 text-sm text-[#111b21] focus:ring-0 outline-none transition-all placeholder:text-gray-400"
                disabled={sending || !activeConversation}
              />
            </div>

            <button
              type="submit"
              disabled={sending || !activeConversation || (!newMessage.trim() && !fileToUpload)}
              className="w-11 h-11 bg-chafadi-primary text-white rounded-full flex items-center justify-center shadow-lg shadow-orange-100 hover:scale-105 active:scale-95 transition-all disabled:opacity-40 disabled:scale-100 disabled:shadow-none shrink-0"
            >
              {sending ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <FiSend size={18} className="translate-x-[1px] -translate-y-[0.5px]" />
              )}
            </button>
          </form>
        </footer>
      </main>

      {/* Shared Components */}
      {viewerSrc && (
        <ImageViewer 
          src={viewerSrc} 
          type={viewerType} 
          onClose={closeViewer} 
        />
      )}
    </div>
  );
};

export default ChatPage;
